import { Check, Info } from 'lucide-react';
import type { PluginManifest } from '@reflex/plugin-api';
import { MetaListItem } from '@reflex/ui/components/shared/MetaListItem';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { findOverviewCardForPlugin } from './findOverviewCardForPlugin';

interface PluginAboutSectionProps {
  manifest: PluginManifest | undefined;
  /**
   * Optional installation-row description, used as a single-paragraph
   * fallback when the plugin has not contributed a marketing-style
   * overview card. The detail page passes its installation description
   * here; if the plugin has a richer overview card we ignore the
   * fallback and render the card's `details` + `features` instead.
   */
  fallbackDescription?: string;
}

/**
 * Renders the rich "About this plugin" section on the org plugin detail
 * page. When the plugin's manifest exposes an {@link PluginOverviewCard}
 * with `details` + `features`, this surfaces the deeper paragraph and
 * bullet list. When neither rich copy nor a fallback description is
 * available, the section renders nothing.
 */
export function PluginAboutSection({ manifest, fallbackDescription }: PluginAboutSectionProps) {
  const card = manifest ? findOverviewCardForPlugin(manifest) : undefined;
  const details = card?.details;
  const features = card?.features ?? [];
  const hasRichCopy = Boolean(details) || features.length > 0;

  if (!hasRichCopy) {
    if (!fallbackDescription) return null;
    return (
      <SectionCard data-testid="plugin-about-section" title="About this plugin" icon={<Info />}>
        <p className="text-sm leading-relaxed text-foreground/80">{fallbackDescription}</p>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      data-testid="plugin-about-section"
      title="About this plugin"
      icon={<Info />}
      contentClassName="space-y-4"
    >
      {details && (
        <p
          data-testid="plugin-about-details"
          className="text-sm leading-relaxed text-foreground/80"
        >
          {details}
        </p>
      )}
      {features.length > 0 && (
        <ul data-testid="plugin-about-features" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {features.map((feature) => (
            <MetaListItem key={feature} variant="subtle" align="start">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-success" />
              <span className="text-xs text-foreground/80">{feature}</span>
            </MetaListItem>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
